import React from "react";

/**
 * Karta.
 *
 * Props zůstávají stejné jako před redesignem (`title`, `action`, `children`,
 * `className`, `onHeaderClick`), takže se nemuselo sáhnout do žádné z desítek
 * stránek, které ji používají. Přibyly jen nepovinné.
 *
 * Nadpis má vždycky pruh přes celou šířku karty, protože holý tučný text byl
 * na stránce nevýrazný a nebylo poznat, kde sekce začíná (rozhodnutí z fáze 0,
 * mockup docs/mockupy/obrazovky/08-karty.html):
 *
 *   výchozí     světlý pruh `bg-brand-50` s ikonou a tmavým nadpisem
 *   `lista`     plná fialová lišta, pro hlavní sekce stránky a všude, kde je
 *               odznak s počtem, akce v liště nebo listování obdobím
 *
 * Pruh se roztahuje přes okraje karty zápornými okraji, takže karta si drží
 * `rounded-2xl` a `p-4 sm:p-5` a tiskové styly, které na tyhle třídy cílí,
 * zůstávají nedotčené. V tisku se pruh zplošťuje (viz src/index.css).
 */
export default function Card({
  title,
  action,
  actionPlna = false,
  children,
  className = "",
  onHeaderClick,
  // nové, nepovinné
  ikona: Ikona,
  lista = false,
  odznak,
  odznakTon = "bila",
  listovani,
}) {
  const klikaci = typeof onHeaderClick === "function";
  const naKlik = klikaci
    ? (e) => {
        // Klepnutí přímo na ovládací prvek si řeší ten prvek sám.
        if (e.target.closest && e.target.closest("button, a, input, select, textarea, label")) return;
        onHeaderClick();
      }
    : undefined;

  const tridaOdznaku = `shrink-0 rounded-lg px-2.5 py-1 text-xs font-bold tabular-nums ${
    odznakTon === "zluta"
      ? "bg-brand-gold text-brand-dark"
      : lista ? "bg-white/20 text-white" : "bg-brand-light text-brand-deep"
  }`;

  const pruh = lista
    // Plná lišta: nese ikonu, název, odznak i listování obdobím.
    ? "bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 text-white"
    // Světlý pruh: klidnější, pro karty, kterých je na stránce hodně vedle sebe.
    : "border-b border-brand/15 bg-brand-50 text-brand-950";

  return (
    <div
      className={`glass min-w-0 overflow-hidden p-4 sm:p-5 ${className}`}
    >
      {(title || action) && (
        <div
          data-pruh-karty
          data-nadpis-karty={lista ? true : undefined}
          onClick={naKlik}
          className={`-mx-4 -mt-4 mb-4 flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:-mx-5 sm:-mt-5 sm:mb-5 sm:px-5 ${pruh} ${
            klikaci ? "cursor-pointer select-none" : ""
          }`}
        >
          {title && (
            <div className="flex min-w-0 items-center gap-3">
              {Ikona && (
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${lista ? "bg-white/20" : "bg-brand-light text-brand-deep"}`}>
                  <Ikona className="h-5 w-5" />
                </span>
              )}
              {/* 12.9.2026: plná lišta má patkový nadpis jako jediná tmavá
                  hlavička karty v adminu. Světlý pruh zůstává bezpatkový. */}
              <h3 className={`min-w-0 font-bold ${lista ? "font-serif text-lg leading-tight" : "text-base"}`}>{title}</h3>
              {odznak != null && <span className={tridaOdznaku} data-odznak-karty data-odznak-ton={odznakTon}>{odznak}</span>}
            </div>
          )}
          {(action || listovani) && (
            <div className={`flex flex-wrap items-center gap-2 ${actionPlna ? "w-full sm:w-auto" : ""}`}>
              {listovani}
              {action}
            </div>
          )}
        </div>
      )}
      {children}
    </div>
  );
}
